import { useRef } from "react";
import { Upload, X, Images } from "lucide-react";
import { toast } from "sonner";
import { downscaleImage } from "./helpers";

const MAX_FILE_BYTES = 12 * 1024 * 1024;

export const AssetLibrary = ({ assets, onChange }) => {
  const inputRef = useRef(null);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /^image\//.test(f.type));
    if (!files.length) return;
    const added = [];
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} supera 12MB`);
        continue;
      }
      try {
        const dataUrl = await downscaleImage(f);
        added.push({
          id: `a${Math.random().toString(36).slice(2, 8)}`,
          name: f.name.replace(/\.[^.]+$/, "").slice(0, 60),
          type: "image",
          dataUrl,
        });
      } catch (e) {
        toast.error(`No se pudo leer ${f.name}`);
      }
    }
    if (added.length) {
      onChange([...assets, ...added]);
      toast.success(`${added.length} asset(s) agregados a la biblioteca`);
    }
  };

  return (
    <div data-testid="planner-asset-library" className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Images className="h-4 w-4 text-[#FF8A00]" />
          <h2 className="font-display text-base font-extrabold tracking-tight">Biblioteca del plan</h2>
        </div>
        <span className="text-[11px] text-zinc-500">{assets.length} assets</span>
      </div>

      <button
        type="button"
        data-testid="planner-upload-assets"
        onClick={() => inputRef.current?.click()}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 px-4 py-3 text-xs font-semibold text-zinc-300 hover:border-[#FF8A00]/50 hover:text-white transition"
      >
        <Upload className="h-4 w-4" />
        Subir imágenes propias (multi)
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="planner-asset-input"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
      />

      {assets.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {assets.map((a) => (
            <div key={a.id} className="relative group rounded-lg overflow-hidden border border-white/10">
              <img src={a.dataUrl} alt={a.name} className="h-20 w-full object-cover" />
              <button
                type="button"
                data-testid={`planner-asset-remove-${a.id}`}
                onClick={() => onChange(assets.filter((x) => x.id !== a.id))}
                className="absolute top-1 right-1 rounded-md bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition"
              >
                <X className="h-3 w-3 text-white" />
              </button>
              <span className="absolute bottom-0 inset-x-0 bg-black/70 px-1 py-0.5 text-[9px] text-zinc-300 truncate">{a.name}</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-zinc-500">
        Subí tus fotos antes de generar la matriz: la IA las asignará a las piezas que las necesiten.
      </p>
    </div>
  );
};
